import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { TableCellType } from "components/LazyTable";
import Loader from "components/Loader";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  ILicenseTrackerAvailabilityItem,
  OrganizationLicenseTrackerLicenseStatusEnum,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { convertToZonedDateTime } from "utils/dateUtils";
import styles from "./styles";
import TrackerStatusDialog from "./TrackerStatusDialog";

type TrackerAvailabilityPaneProps = {
  licenseId: string;
  canSetAvailability: boolean;
};

const statusChip = (status?: OrganizationLicenseTrackerLicenseStatusEnum) => {
  switch (status) {
    case OrganizationLicenseTrackerLicenseStatusEnum.Released:
      return { color: "success" as const, key: "released" };
    case OrganizationLicenseTrackerLicenseStatusEnum.ComingSoon:
      return { color: "warning" as const, key: "comingSoon" };
    default:
      return { color: "default" as const, key: "notPlanned" };
  }
};

// A node cell is handed the whole row, not the value at its accessor, so these read the row's own
// fields and the row carries its own handlers.
const StatusCell = ({ value }: { value: any }) => {
  const { formatMessage: t } = useIntl();
  const chip = statusChip(value.status);

  return <Chip size="small" color={chip.color} label={t({ id: chip.key })} />;
};

const SetByCell = ({ value }: { value: any }) =>
  value.setBy ? (
    <>
      <div>{value.setBy}</div>
      <Typography variant="caption" color="text.secondary">
        {/* When someone set this — a UTC moment, shown in the portal's zone. */}
        {convertToZonedDateTime(value.setOn)}
      </Typography>
    </>
  ) : (
    <>—</>
  );

const ActionsCell = ({ value }: { value: any }) => {
  const { formatMessage: t } = useIntl();

  return (
    <Box display="flex" justifyContent="flex-end">
      {value.onEdit ? (
        <Tooltip title={t({ id: "setRoadmapStatus" })}>
          <IconButton size="small" onClick={value.onEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title={t({ id: "requiresTrackerEnableClaim" })}>
          <Chip size="small" label={t({ id: "noAccess" })} />
        </Tooltip>
      )}
    </Box>
  );
};

const TrackerAvailabilityPane = ({
  licenseId,
  canSetAvailability,
}: TrackerAvailabilityPaneProps) => {
  const { formatMessage: t } = useIntl();
  const { licenseTrackerService } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const [editing, setEditing] = useState<ILicenseTrackerAvailabilityItem>();

  // Keyed on the header organization: pointing the header somewhere else is what changes the list,
  // and the page stays where it is.
  const availabilityResult = useQuery(
    ["licenseTrackerService.getLicenseTrackerAvailability", licenseId, organizationId],
    () => licenseTrackerService.getLicenseTrackerAvailability(licenseId)
  );

  if (availabilityResult.isLoading) return <Loader />;

  const availability = availabilityResult.data?.data;
  const organizations = availability?.organizations ?? [];

  const cells: Array<TableCellType> = [
    {
      accessor: "organizationName",
      type: "text",
      label: t({ id: "brandOrganization" }),
    },
    {
      accessor: "status",
      type: "node",
      label: t({ id: "roadmapStatus" }),
      View: StatusCell,
    },
    {
      accessor: "setOn",
      type: "node",
      label: t({ id: "setBy" }),
      View: SetByCell,
    },
    {
      accessor: "actions",
      align: "right",
      type: "node",
      noSort: true,
      label: t({ id: "actions" }),
      View: ActionsCell,
    },
  ];

  const rows = organizations.map((item) => ({
    ...item,
    id: item.organizationId ?? "",
    // The replica supplies the name; a brand it has not synced yet still has to be listed.
    organizationName: item.organizationName ?? item.organizationId,
    onEdit: canSetAvailability ? () => setEditing(item) : undefined,
  }));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={styles.paneHead}>
        <Typography variant="h6">
          {t({ id: "licenseTrackerAvailability" })}
        </Typography>
      </Box>

      <Alert
        severity={availability?.hasGoverningRequirementSet ? "success" : "warning"}
        sx={{ mb: 2 }}
      >
        {t({
          id: availability?.hasGoverningRequirementSet
            ? "requirementsApproved"
            : "noApprovedRequirements",
        })}
      </Alert>

      {rows.length === 0 ? (
        <Alert severity="info">{t({ id: "noBrandOrganizations" })}</Alert>
      ) : (
        <SortableTable
          cells={cells}
          rows={rows}
          dense
          defaultOrderBy="organizationName"
        />
      )}

      {editing && (
        <TrackerStatusDialog
          licenseId={licenseId}
          item={editing}
          open
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            availabilityResult.refetch();
          }}
        />
      )}
    </Paper>
  );
};

export default TrackerAvailabilityPane;
